import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { LOCATIONS } from "@/content/locations";
import { LocationPageTemplate } from "@/components/locations/LocationPageTemplate";
import { pageMeta, ldScripts, breadcrumbLd, faqLd, localBusinessLd, SOCIAL_PROFILES } from "@/lib/seo";
import { abMeta } from "@/content/metaExperiments";
import { siteConfig } from "@/config/site";


export const Route = createFileRoute("/seo-agency-{$slug}")({
  loader: async ({ params }) => {
    const { getLocation } = await import("@/content/locations");
    const location = getLocation((params as any).slug);
    if (!location) throw notFound();
    return { location };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: `Location not found — ${siteConfig.name}` },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const l = loaderData.location;
    const path = `/seo-agency-${(params as any).slug}/`;
    const ab = abMeta(path, { title: l.metaTitle, description: l.metaDescription });
    const base = pageMeta({
      path,
      title: ab.title,
      description: ab.description,
      image: `${siteConfig.origin}/og-image.png`,
      type: "website",
    });
    return {
      ...base,
      scripts: ldScripts(
        localBusinessLd({
          city: siteConfig.contact.address.city,
          path: `/seo-agency-${siteConfig.contact.address.city.toLowerCase()}/`,
          description: `${siteConfig.name} ${siteConfig.contact.address.city} HQ - Leading SEO & AI Search Agency.`,
          sameAs: [
            ...SOCIAL_PROFILES,
            "https://maps.app.goo.gl/L67bz47dQgtJs4Ba8"
          ]
        }),


        breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Locations", path: "/locations/" },
          { name: `${l.city}, ${siteConfig.contact.address.state} SEO Agency`, path },
        ]),
        localBusinessLd({
          city: l.city,
          region: siteConfig.contact.address.state,
          path,
          description: l.metaDescription,
          latLng: l.latLng,
          areaServed: l.coverage.map((c: { name: string }) => c.name),
          kgMid: l.slug === siteConfig.contact.address.city.toLowerCase() ? "/g/11zb51w5c6" : undefined,
        }),

        ...(l.faqs?.length ? [faqLd(l.faqs)] : []),
      ),
    };
  },
  notFoundComponent: LocationNotFound,
  errorComponent: LocationError,
  component: LocationPage,
});

function LocationPage() {
  const { location } = Route.useLoaderData();
  return <LocationPageTemplate location={location} />;
}

function LocationNotFound() {
  return (
    <div className="container-page py-24">
      <h1 className="font-display text-4xl">Location not found</h1>
      <p className="mt-3 text-muted-foreground">
        We may not cover that market yet. See all locations below.
      </p>
      <ul className="mt-6 space-y-2">
        {LOCATIONS.map((l) => (
          <li key={l.slug} className="text-primary">
            <Link to={`/seo-agency-${l.slug}/` as any}>{l.city}, TX</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LocationError({ error, reset }: import("@tanstack/react-router").ErrorComponentProps) {
  return (
    <div className="container-page py-24">
      <h1 className="font-display text-4xl">Something went wrong</h1>
      <p className="mt-3 text-muted-foreground">{(error as Error).message}</p>
      <button
        onClick={reset}
        className="mt-6 rounded-full border border-border-strong px-5 py-2 text-sm hover:border-primary hover:text-primary"
      >
        Retry
      </button>
    </div>
  );
}
